import { browserSupportsWebAuthn, startAuthentication, startRegistration } from "@simplewebauthn/browser";

/**
 * Passkey-kirjautuminen ja -rekisteröinti. Napit tunnistetaan data-attribuuteilla:
 *  - [data-passkey-login]    kirjautumissivulla (lukee sähköpostin samasta lomakkeesta)
 *  - [data-passkey-register] omissa asetuksissa (nimen kenttä input[name="passkey_label"] samassa [data-passkey-scope]-alueessa)
 * Viestit tulevat napin data-msg-*-attribuuteista, jotta tekstit pysyvät käännöstiedostoissa.
 */

const csrf = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? "";

function status(el: HTMLElement | null, text: string, error = false): void {
  if (!el) return;
  el.textContent = text;
  el.classList.toggle("error", error);
}

async function post<T>(url: string, body: unknown): Promise<T> {
  const res = await fetch(url, {
    method: "POST",
    headers: { "content-type": "application/json", "x-csrf-token": csrf },
    body: JSON.stringify(body),
    credentials: "same-origin",
  });
  if (!res.ok) {
    let message = `${res.status}`;
    try {
      const data = (await res.json()) as { error?: { message?: string } };
      message = data.error?.message ?? message;
    } catch {}
    throw new Error(message);
  }
  return (await res.json()) as T;
}

function wireLogin(btn: HTMLButtonElement): void {
  const form = btn.closest("form");
  const statusEl = form?.querySelector<HTMLElement>("[data-passkey-status]") ?? null;
  btn.hidden = false;
  btn.addEventListener("click", async () => {
    const email = form?.querySelector<HTMLInputElement>('input[name="email"]')?.value.trim() || undefined;
    btn.disabled = true;
    status(statusEl, btn.dataset.msgWait ?? "");
    try {
      const options = await post<Parameters<typeof startAuthentication>[0]["optionsJSON"]>(
        "/api/v1/auth/passkey/login/options",
        { email },
      );
      const response = await startAuthentication({ optionsJSON: options });
      const result = await post<{ ok: boolean; redirect: string }>("/api/v1/auth/passkey/login/verify", {
        response,
        next: btn.dataset.next || undefined,
      });
      window.location.assign(result.redirect || "/");
    } catch (err) {
      status(
        statusEl,
        `${btn.dataset.msgError ?? ""} ${err instanceof Error ? err.message : ""}`.trim(),
        true,
      );
      btn.disabled = false;
    }
  });
}

function wireRegister(btn: HTMLButtonElement): void {
  const scope = btn.closest("[data-passkey-scope]") ?? document;
  const statusEl = scope.querySelector<HTMLElement>("[data-passkey-status]");
  const labelInput = scope.querySelector<HTMLInputElement>('input[name="passkey_label"]');
  btn.hidden = false;
  btn.addEventListener("click", async () => {
    btn.disabled = true;
    status(statusEl, btn.dataset.msgWait ?? "");
    try {
      const options = await post<Parameters<typeof startRegistration>[0]["optionsJSON"]>(
        "/api/v1/auth/passkey/register/options",
        {},
      );
      const response = await startRegistration({ optionsJSON: options });
      const label = labelInput?.value.trim() || undefined;
      const result = await post<{ ok: boolean; message: string }>("/api/v1/auth/passkey/register/verify", {
        response,
        label,
      });
      const url = new URL(window.location.href);
      url.searchParams.set("ok", result.message);
      window.location.assign(url.toString());
    } catch (err) {
      status(
        statusEl,
        `${btn.dataset.msgError ?? ""} ${err instanceof Error ? err.message : ""}`.trim(),
        true,
      );
      btn.disabled = false;
    }
  });
}

if (browserSupportsWebAuthn()) {
  for (const btn of document.querySelectorAll<HTMLButtonElement>("[data-passkey-login]")) wireLogin(btn);
  for (const btn of document.querySelectorAll<HTMLButtonElement>("[data-passkey-register]"))
    wireRegister(btn);
} else {
  for (const el of document.querySelectorAll<HTMLElement>("[data-passkey-unsupported]")) el.hidden = false;
}
